// ReminderNudgeTile.tsx — a Craig-fronted Today-screen tile inviting the user to
// set a gentle reminder to "turn up for themselves". Reminders were removed from
// onboarding (FirstSession); this surfaces them softly instead, just beneath the
// arriving check-in. Tapping opens a small notifications popup (ReminderNudgeSheet)
// to set a daily nudge or skip; the × hides the tile (reshowable from You, then
// Reminders).
import React from 'react';
import { Pressable, Btn } from '@/components/ui';
import { Icon } from '@/components/icons';
import { clipUrl } from '@/data/guideMessages';
import { useProgress } from '@/hooks/useProgress';

// A different Craig image to the arriving check-in (which uses 'self-care').
const NUDGE_CLIP = 'welcome-back';

const PRESETS: { label: string; time: string }[] = [
  { label: 'Morning', time: '08:00' },
  { label: 'Afternoon', time: '13:00' },
  { label: 'Evening', time: '19:00' },
];

// ── the notifications popup (replaces routing to the full Settings page) ──
export function ReminderNudgeSheet({ onClose }: { onClose: () => void }) {
  const progress = useProgress();
  const [time, setTime] = React.useState('19:00');

  const setReminder = () => {
    progress.setMorningAnchor(time);
    progress.setReminderNudgeDismissed(true); // done nudging — they've set one
    onClose();
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Set a reminder" style={overlay}>
      <div style={panel}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
          <img src={clipUrl(NUDGE_CLIP)} alt="" aria-hidden="true" style={{ width: 92, height: 92, objectFit: 'contain' }} />
        </div>
        <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 22, fontWeight: 500, textAlign: 'center', color: 'var(--ink)', marginBottom: 6 }}>
          Want me to nudge you back?
        </div>
        <p style={{ textAlign: 'center', color: 'var(--ink-soft)', fontSize: 14, lineHeight: 1.55, margin: '0 0 18px', fontFamily: 'Inter, system-ui, sans-serif' }}>
          A soft daily nudge, at a time that suits you. You can change or turn it off any time in You, then Reminders.
        </p>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
          {PRESETS.map((p) => {
            const on = time === p.time;
            return (
              <button key={p.time} type="button" onClick={() => setTime(p.time)} style={chip(on)}>
                {p.label}
              </button>
            );
          })}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--ink-soft)', fontFamily: 'Inter, system-ui, sans-serif' }}>or a time</span>
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} aria-label="Reminder time" style={timeInput} />
        </label>

        <p style={{ textAlign: 'center', color: 'var(--ink-soft)', fontSize: 11.5, lineHeight: 1.5, margin: '0 0 18px', fontFamily: 'Inter, system-ui, sans-serif' }}>
          Reminders fire on the iOS and Android app.
        </p>

        <Btn full size="lg" onClick={setReminder} ariaLabel="Set my reminder">Set reminder</Btn>
        <button type="button" onClick={onClose} style={skipBtn}>Skip for now</button>
      </div>
    </div>
  );
}

export function ReminderNudgeTile() {
  const progress = useProgress();
  const [sheetOpen, setSheetOpen] = React.useState(false);
  if (progress.state.reminderNudgeDismissed) return null;

  return (
    <div style={{ position: 'relative' }}>
      <Pressable
        as="button"
        onClick={() => setSheetOpen(true)}
        ariaLabel="Set a gentle reminder — would you like a soft nudge back?"
        style={{
          display: 'flex',
          alignItems: 'stretch',
          width: '100%',
          textAlign: 'left',
          borderRadius: 16,
          overflow: 'hidden',
          border: '1px solid var(--hair)',
          background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
          boxShadow: 'var(--edge), var(--sh1)',
          cursor: 'pointer',
          minHeight: 100,
        }}
      >
        {/* Craig, on the left */}
        <span
          style={{
            flex: '0 0 33%',
            maxWidth: 130,
            minWidth: 94,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            background: 'rgba(29,158,117,0.10)',
          }}
        >
          <img
            src={clipUrl(NUDGE_CLIP)}
            alt=""
            aria-hidden="true"
            style={{ width: '100%', height: '100%', maxHeight: 124, objectFit: 'contain', objectPosition: 'bottom center' }}
          />
        </span>

        {/* the invitation */}
        <span style={{ flex: 1, minWidth: 0, padding: '12px 14px 13px', paddingRight: 40, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 5 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--green-deep)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
            <Icon name="clock" size={12} /> A gentle nudge
          </span>
          <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 16.5, fontWeight: 500, lineHeight: 1.22, color: 'var(--ink)' }}>
            Want me to nudge you back?
          </span>
          <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink-soft)' }}>
            Showing up is the hard part. Let me send you a soft nudge, at a time that suits you.
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 3, color: 'var(--green-deep)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, fontWeight: 700 }}>
            Set a reminder <Icon name="chevRight" size={14} />
          </span>
        </span>
      </Pressable>

      {/* dismiss — hides the tile; reshowable from You → Reminders */}
      <button
        type="button"
        aria-label="Hide this. You can bring it back from You, then Reminders."
        onClick={() => progress.setReminderNudgeDismissed(true)}
        style={{
          position: 'absolute',
          top: 7,
          right: 7,
          padding: '3px 9px',
          borderRadius: 999,
          border: 'none',
          background: 'rgba(247,245,240,0.92)',
          color: 'var(--ink-soft)',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 11,
          fontWeight: 600,
          cursor: 'pointer',
          boxShadow: '0 1px 4px rgba(0,0,0,0.14)',
        }}
      >
        Hide
      </button>

      {sheetOpen && <ReminderNudgeSheet onClose={() => setSheetOpen(false)} />}
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1100,
  background: 'rgba(20,16,12,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  paddingTop: 'calc(var(--safe-top) + 24px)',
  paddingBottom: 'calc(var(--safe-bottom) + 24px)',
};
const panel: React.CSSProperties = {
  width: '100%',
  maxWidth: 400,
  background: 'var(--card-top, #fff)',
  border: '1px solid var(--hair-strong, rgba(0,0,0,0.1))',
  borderRadius: 20,
  padding: 24,
  maxHeight: '90vh',
  overflowY: 'auto',
};
function chip(on: boolean): React.CSSProperties {
  return {
    padding: '9px 16px',
    borderRadius: 999,
    border: on ? '1px solid var(--green-deep)' : '1px solid var(--hair)',
    background: on ? 'var(--green-bg, #EAF3EC)' : 'var(--cream)',
    color: on ? 'var(--green-deep)' : 'var(--ink)',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: 13.5,
    fontWeight: 600,
    cursor: 'pointer',
  };
}
const timeInput: React.CSSProperties = {
  padding: '9px 12px',
  borderRadius: 11,
  border: '1px solid var(--hair)',
  background: 'var(--cream)',
  color: 'var(--ink)',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 15,
};
const skipBtn: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 12,
  padding: '10px',
  border: 'none',
  background: 'none',
  color: 'var(--ink-soft)',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};
